import React from 'react';
import { Twitch, Youtube, Instagram } from 'lucide-react';
import { streamer } from '../mock';

/**
 * Social icons cluster styled in the same JorDieVoorJe logo language:
 * pink/purple gradient border, glassy dark fill, italic slant matching the
 * logo. Positioned to overlap the bottom-right of the hero logo so the two
 * elements blend into one badge.
 */

function DiscordGlyph({ className = '' }) {
  return (
    <svg viewBox="0 0 24 24" className={className} fill="currentColor" aria-hidden>
      <path d="M20.317 4.369A19.79 19.79 0 0 0 16.558 3l-.153.32c1.35.297 2.658.755 3.882 1.361-2.31-1.05-4.98-1.05-7.32-1.05-2.34 0-5.01 0-7.32 1.05a17.4 17.4 0 0 1 3.883-1.361L9.44 3a19.6 19.6 0 0 0-3.76 1.369C1.75 8.51.917 12.54 1.35 16.52c2.02 1.5 3.98 2.42 5.91 3.02.48-.66.9-1.36 1.27-2.1-.66-.24-1.29-.54-1.9-.9.16-.12.32-.24.47-.36 3.6 1.68 7.5 1.68 11.1 0 .15.12.31.24.47.36-.61.36-1.24.66-1.9.9.37.74.79 1.44 1.27 2.1 1.93-.6 3.9-1.52 5.91-3.02.55-4.72-1.02-8.71-4.55-12.15ZM8.65 14.4c-.83 0-1.5-.77-1.5-1.72s.66-1.71 1.5-1.71c.83 0 1.5.77 1.5 1.72s-.67 1.71-1.5 1.71Zm6.7 0c-.83 0-1.5-.77-1.5-1.72s.66-1.71 1.5-1.71c.83 0 1.5.77 1.5 1.72s-.67 1.71-1.5 1.71Z" />
    </svg>
  );
}

function TikTokGlyph({ className = '' }) {
  return (
    <svg viewBox="0 0 24 24" className={className} fill="currentColor" aria-hidden>
      <path d="M19.59 6.69a4.83 4.83 0 0 1-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 0 1-5.2 1.74 2.89 2.89 0 0 1 2.31-4.64 2.93 2.93 0 0 1 .88.13V9.4a6.84 6.84 0 0 0-1-.05A6.33 6.33 0 0 0 5.8 20.1a6.34 6.34 0 0 0 10.86-4.43V8.75a8.16 8.16 0 0 0 4.77 1.52V6.83a4.85 4.85 0 0 1-1.84-.14Z" />
    </svg>
  );
}

function KickGlyph({ className = '' }) {
  return (
    <svg viewBox="0 0 24 24" className={className} fill="currentColor" aria-hidden>
      <path d="M2 3h6v5h2V3h6v3h2v3h2v6h-2v3h-2v3h-6v-5H8v5H2V3Z" />
    </svg>
  );
}

function XGlyph({ className = '' }) {
  return (
    <svg viewBox="0 0 24 24" className={className} fill="currentColor" aria-hidden>
      <path d="M18.244 2H21l-6.55 7.485L22 22h-6.9l-4.71-6.155L4.8 22H2l7.02-8.025L2 2h6.98l4.32 5.71L18.244 2Zm-2.44 18h1.76L7.28 4H5.42l10.38 16Z" />
    </svg>
  );
}

const ITEMS = [
  { key: 'twitch',    label: 'Twitch',    url: streamer.socials.twitch,    icon: Twitch,        color: '#c084fc' },
  { key: 'youtube',   label: 'YouTube',   url: streamer.socials.youtube,   icon: Youtube,       color: '#ff5c8a' },
  { key: 'discord',   label: 'Discord',   url: streamer.socials.discord,   icon: DiscordGlyph,  color: '#a78bfa' },
  { key: 'tiktok',    label: 'TikTok',    url: streamer.socials.tiktok,    icon: TikTokGlyph,   color: '#f472b6' },
  { key: 'instagram', label: 'Instagram', url: streamer.socials.instagram, icon: Instagram,     color: '#f472b6' },
  { key: 'kick',      label: 'Kick',      url: streamer.socials.kick,      icon: KickGlyph,     color: '#c084fc' },
  { key: 'twitter',   label: 'X',         url: streamer.socials.twitter,   icon: XGlyph,        color: '#e9d5ff' },
];

export default function HeroSocials() {
  return (
    <div
      className="absolute right-[4%] bottom-[-6%]"
      style={{ transform: 'rotate(0deg)', transformOrigin: 'top left' }}
      data-testid="hero-socials"
    >
      <div
        className="relative flex items-center gap-1 px-3 py-1.5"
        style={{
          background:
            'linear-gradient(#0b0217, #0b0217) padding-box, ' +
            'linear-gradient(135deg, #7c3aed, #d946ef 45%, #f472b6 75%, #c084fc) border-box',
          border: '3px solid transparent',
          borderRadius: '14px',
          boxShadow: '0 14px 30px -12px rgba(232, 121, 249, 0.6)',
        }}
      >
        {ITEMS.filter((i) => Boolean(i.url)).map((item) => {
          const Icon = item.icon;
          return (
            <a
              key={item.key}
              href={item.url}
              target="_blank"
              rel="noreferrer noopener"
              aria-label={item.label}
              data-testid={`hero-social-${item.key}`}
              className="group relative w-8 h-8 flex items-center justify-center rounded-lg transition-all duration-200 hover:-translate-y-0.5"
              title={item.label}
            >
              <span
                className="absolute inset-0 rounded-lg opacity-0 group-hover:opacity-100 transition-opacity"
                style={{
                  background: `radial-gradient(circle at center, ${item.color}55, transparent 70%)`,
                  filter: 'blur(4px)',
                }}
                aria-hidden
              />
              <Icon
                className="w-[17px] h-[17px] relative z-10 transition-colors"
                style={{ color: item.color }}
              />
            </a>
          );
        })}
      </div>
    </div>
  );
}
